import type { FC, RefObject } from 'react'
import { useEffect, useMemo } from 'react'
import { RobotOutlined } from '@ant-design/icons'
import { alpha, Box, Stack, Typography, useTheme } from '@mui/material'
import { motion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import Paper from 'components/@extended/Paper'
import type { AgentMessage } from '../types'

const MAX_USER_MSG_WIDTH = 560

interface ImportConversationProps {
  messages: AgentMessage[]
  scrollAnchorRef?: RefObject<HTMLDivElement | null>
  maxWidth?: number
  isThinking?: boolean
  showThinking?: boolean
  autoScroll?: boolean
}

const ImportConversation: FC<ImportConversationProps> = ({
  messages,
  scrollAnchorRef,
  maxWidth,
  isThinking = false,
  showThinking = true,
  autoScroll = true,
}) => {
  const theme = useTheme()
  const groupedMessages = useMemo(() => messages, [messages])
  const shouldShowThinking = showThinking && isThinking
  const hasContent = groupedMessages.length > 0 || shouldShowThinking

  useEffect(() => {
    if (!autoScroll) return
    if (!scrollAnchorRef?.current) return
    scrollAnchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [autoScroll, groupedMessages.length, isThinking, scrollAnchorRef])

  return (
    <Stack spacing={2} sx={{ minHeight: 0, width: '100%' }}>
      <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
        {hasContent && (
          <Stack spacing={2}>
            {groupedMessages.map((msg) => {
              const isUser = msg.role === 'user'
              return (
                <Box
                  key={msg.id}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: isUser ? 'flex-end' : 'flex-start',
                  }}
                >
                  <Paper
                    sx={{
                      p: 2,
                      width: isUser ? 'auto' : '100%',
                      maxWidth: isUser ? MAX_USER_MSG_WIDTH : '100%',
                      backgroundColor: isUser ? theme.palette.primary.main : theme.palette.background.paper,
                      color: isUser ? theme.palette.primary.contrastText : theme.palette.text.primary,
                    }}
                  >
                    <Typography
                      component="div"
                      variant="body2"
                      sx={(theme) => ({
                        '& p': { margin: '0 0 8px' },
                        '& p:last-of-type': { marginBottom: 0 },
                        '& ul': { margin: '6px 0 8px 18px', padding: 0 },
                        '& ol': { margin: '6px 0 8px 18px', padding: 0 },
                        '& li': { marginBottom: '2px' },
                        '& strong': { fontWeight: 600 },
                        '& code': {
                          fontFamily: '"IBM Plex Mono", "Fira Code", monospace',
                          backgroundColor: 'rgba(0, 0, 0, 0.08)',
                          padding: '1px 4px',
                          borderRadius: 4,
                        },
                        '& table': {
                          width: '100%',
                          borderCollapse: 'collapse',
                          marginTop: 8,
                          fontSize: 13,
                        },
                        '& th, & td': {
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          padding: '6px 8px',
                          textAlign: 'left',
                        },
                        '& th': {
                          fontWeight: 600,
                        },
                      })}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                    </Typography>
                  </Paper>
                </Box>
              )
            })}
            {shouldShowThinking && <ThinkingIndicator />}
            {scrollAnchorRef && <div ref={scrollAnchorRef} />}
          </Stack>
        )}
      </Box>
    </Stack>
  )
}

export const ThinkingIndicator: FC = () => {
  const theme = useTheme()
  const orbitRadius = 34
  const orbitDots = Array.from({ length: 8 }, (_, index) => ({ angle: index * 45 }))
  const dotPalette = [
    alpha(theme.palette.primary.main, 0.9),
    alpha(theme.palette.info.main, 0.9),
    alpha(theme.palette.success.main, 0.9),
    alpha(theme.palette.warning.main, 0.9),
  ]

  return (
    <Stack spacing={1.4} alignItems="center" sx={{ py: 1, textAlign: 'center' }}>
      <Box
        sx={{
          position: 'relative',
          width: 104,
          height: 104,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <motion.div
          animate={{ y: [0, -3, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          style={{ position: 'relative', zIndex: 1 }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <RobotOutlined style={{ fontSize: 28, color: theme.palette.primary.main }} />
          </Box>
        </motion.div>
        {orbitDots.map((dot, index) => (
          <Box
            key={`orbit-dot-${index}`}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: `translate(-50%, -50%) rotate(${dot.angle}deg) translateY(-${orbitRadius}px)`,
            }}
          >
            <motion.span
              animate={{
                opacity: [0.4, 1, 0.4],
                scale: [0.7, 1.15, 0.7],
                backgroundColor: [
                  dotPalette[index % dotPalette.length],
                  dotPalette[(index + 1) % dotPalette.length],
                  dotPalette[index % dotPalette.length],
                ],
              }}
              transition={{
                duration: 2.6,
                repeat: Infinity,
                delay: index * 0.18,
                ease: 'easeInOut',
              }}
              style={{
                display: 'block',
                width: 10,
                height: 10,
                borderRadius: '50%',
                boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.25)}`,
              }}
            />
          </Box>
        ))}
      </Box>
      <Stack spacing={0.4} alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Preparing your draft
        </Typography>
      </Stack>
    </Stack>
  )
}

export default ImportConversation
